import { useMemo } from 'react'
import { BookOpen, X, Flame } from 'lucide-react'
import { useGameStore } from '@/stores/game.store'
import { useAuthStore } from '@/stores/auth.store'
import { useProgressStore } from '@/stores/progress.store'
import { computeTotalXp, computeLevel, computeStreak } from '@/lib/xp'

interface HUDProps {
  algorithmTitle: string
  chapterLabel: string
  panelOpen?: boolean
  onTogglePanel?: () => void
}

export function HUD({ algorithmTitle, chapterLabel, panelOpen, onTogglePanel }: HUDProps) {
  const { stepIndex, steps, status, raceStartedAt, elapsedMs } = useGameStore()
  const { user } = useAuthStore()
  const { progressMap } = useProgressStore()
  const totalSteps = steps.length

  const displayMs =
    status === 'completed'
      ? elapsedMs
      : raceStartedAt
      ? Date.now() - raceStartedAt
      : 0

  const seconds = Math.floor(displayMs / 1000)
  const ms = Math.floor((displayMs % 1000) / 10).toString().padStart(2, '0')

  const { level, xpProgress } = useMemo(() => {
    const totalXp = computeTotalXp(progressMap)
    return computeLevel(totalXp)
  }, [progressMap])

  const streak = useMemo(() => computeStreak(progressMap), [progressMap])

  return (
    <header className="sticky top-0 z-10 h-14 bg-arcade-surface/90 backdrop-blur border-b border-arcade-border px-4 flex items-center gap-4 relative">
      {/* Title */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 font-mono leading-none mb-0.5">{chapterLabel}</p>
        <p className="text-sm font-bold text-white truncate leading-none">{algorithmTitle}</p>
      </div>

      {/* Step counter */}
      <div className="text-center hidden sm:block">
        <p className="text-xs text-gray-500 font-mono leading-none mb-0.5">Step</p>
        <p className="text-sm font-mono font-bold text-arcade-neon leading-none">
          {totalSteps > 0 ? `${stepIndex + 1} / ${totalSteps}` : '—'}
        </p>
      </div>

      {/* Race timer */}
      {(status === 'playing' || status === 'paused' || status === 'completed') && (
        <div className="text-center hidden sm:block">
          <p className="text-xs text-gray-500 font-mono leading-none mb-0.5">Time</p>
          <p className="text-sm font-mono font-bold text-yellow-400 leading-none">
            {seconds}:{ms}
          </p>
        </div>
      )}

      {/* Streak — only when logged in and streak > 0 */}
      {user && streak > 0 && (
        <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <Flame size={13} className="text-orange-400" />
          <span className="text-xs font-mono font-bold text-orange-300">{streak}</span>
        </div>
      )}

      {/* Level badge */}
      {user && (
        <div className="flex items-center px-2 py-1 rounded-lg bg-arcade-accent/20 border border-arcade-accent/30">
          <span className="text-xs font-mono font-bold text-violet-300">Lv.{level}</span>
        </div>
      )}

      {/* Status badge */}
      <div
        className={`px-2.5 py-1 rounded-full text-xs font-bold font-mono hidden sm:block ${
          status === 'completed'
            ? 'bg-arcade-neon/20 text-arcade-neon'
            : status === 'playing'
            ? 'bg-violet-500/20 text-violet-300'
            : status === 'paused'
            ? 'bg-yellow-500/20 text-yellow-300'
            : 'bg-gray-800 text-gray-400'
        }`}
      >
        {status.toUpperCase()}
      </div>

      {/* Panel toggle */}
      {onTogglePanel && (
        <button
          onClick={onTogglePanel}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
            panelOpen
              ? 'bg-arcade-accent text-white'
              : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
          }`}
        >
          {panelOpen ? <X size={14} /> : <BookOpen size={14} />}
          <span className="hidden sm:inline">{panelOpen ? 'Close' : 'Learn'}</span>
        </button>
      )}

      {/* XP progress bar — thin strip at bottom edge, always visible when logged in */}
      {user && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800">
          <div
            className="h-full bg-arcade-neon transition-all duration-700 ease-out"
            style={{ width: `${xpProgress * 100}%` }}
          />
        </div>
      )}
    </header>
  )
}
